import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useLanguage } from "../context/LanguageContext";
import { MicrochipTip } from "./MicrochipTip";
import { CalendarDays, MapPin, PawPrint, Cpu, Tag, Sparkles } from "lucide-react";

export const LF_GENDERS = ["male", "female", "neutered_male", "spayed_female", "unknown"];
export const LF_CHIP = ["yes", "no", "unknown"];
export const LF_EMPTY = { lf_date: "", lf_last_seen: "", lf_gender: "", lf_microchip: "", lf_collar: "", lf_markings: "" };

const todayLocal = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

export const LostFoundFields = ({ form, set }) => {
  const { t } = useLanguage();
  const found = form.lf_type === "found";
  return (
    <div className="rounded-2xl border-2 border-dashed border-red-200 bg-red-50/40 p-4 space-y-4" data-testid="lf-details-section">
      <p className="font-display font-bold text-[#12234A]">{t("lfd_section")}</p>
      {found && <MicrochipTip />}
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label>{t(found ? "lfd_date_found" : "lfd_date_missing")} <span className="text-red-500">*</span></Label>
          <Input type="date" max={todayLocal()} value={form.lf_date} onChange={(e) => set("lf_date", e.target.value)} data-testid="lf-date-input" className="mt-1 h-11 rounded-xl bg-white" required />
        </div>
        <div>
          <Label>{t(found ? "lfd_where_found" : "lfd_last_seen")} <span className="text-red-500">*</span></Label>
          <Input value={form.lf_last_seen} maxLength={160} onChange={(e) => set("lf_last_seen", e.target.value)} data-testid="lf-last-seen-input" className="mt-1 h-11 rounded-xl bg-white" placeholder={t("lfd_last_seen_ph")} required />
        </div>
        <div>
          <Label>{t("lfd_gender")}</Label>
          <Select value={form.lf_gender || undefined} onValueChange={(v) => set("lf_gender", v)}>
            <SelectTrigger data-testid="lf-gender-select" className="mt-1 h-11 rounded-xl bg-white"><SelectValue placeholder={t("lfd_select")} /></SelectTrigger>
            <SelectContent className="bg-white">
              {LF_GENDERS.map((g) => <SelectItem key={g} value={g} data-testid={`lf-gender-${g}`}>{t(`lfd_g_${g}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t("lfd_microchip")}</Label>
          <div className="mt-1 grid grid-cols-3 gap-2">
            {LF_CHIP.map((c) => (
              <button type="button" key={c} onClick={() => set("lf_microchip", form.lf_microchip === c ? "" : c)} data-testid={`lf-chip-${c}`}
                className={`h-11 rounded-xl border-2 text-sm font-semibold transition-colors ${form.lf_microchip === c ? "bg-[#12234A] border-[#12234A] text-white" : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                {t(`lfd_c_${c}`)}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div>
        <Label>{t("lfd_collar")}</Label>
        <Input value={form.lf_collar} maxLength={160} onChange={(e) => set("lf_collar", e.target.value)} data-testid="lf-collar-input" className="mt-1 h-11 rounded-xl bg-white" placeholder={t("lfd_collar_ph")} />
      </div>
      <div>
        <Label>{t("lfd_markings")}</Label>
        <Textarea value={form.lf_markings} maxLength={400} rows={2} onChange={(e) => set("lf_markings", e.target.value)} data-testid="lf-markings-input" className="mt-1 rounded-xl bg-white" placeholder={t("lfd_markings_ph")} />
      </div>
    </div>
  );
};

const Fact = ({ icon: Icon, label, value, testid, wide }) => (
  <div className={`flex gap-3 bg-slate-50 rounded-xl p-3 ${wide ? "sm:col-span-2" : ""}`} data-testid={testid}>
    <Icon size={18} className="text-red-500 shrink-0 mt-0.5" />
    <div className="min-w-0">
      <p className="text-xs text-slate-400">{label}</p>
      <p className="font-semibold text-[#12234A] text-sm mt-0.5 break-words">{value}</p>
    </div>
  </div>
);

export const LostFoundFacts = ({ listing }) => {
  const { t, lang } = useLanguage();
  const found = listing.lf_type === "found";
  const date = listing.lf_date
    ? new Date(`${listing.lf_date}T12:00:00`).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { weekday: "short", year: "numeric", month: "long", day: "numeric" })
    : "";
  const facts = [
    date && { icon: CalendarDays, label: t(found ? "lfd_date_found" : "lfd_date_missing"), value: date, testid: "lf-fact-date" },
    listing.lf_last_seen && { icon: MapPin, label: t(found ? "lfd_where_found" : "lfd_last_seen"), value: listing.lf_last_seen, testid: "lf-fact-last-seen" },
    listing.lf_gender && { icon: PawPrint, label: t("lfd_gender"), value: t(`lfd_g_${listing.lf_gender}`), testid: "lf-fact-gender" },
    listing.lf_microchip && { icon: Cpu, label: t("lfd_microchip"), value: t(`lfd_c_${listing.lf_microchip}`), testid: "lf-fact-microchip" },
    listing.lf_collar && { icon: Tag, label: t("lfd_collar"), value: listing.lf_collar, testid: "lf-fact-collar", wide: true },
    listing.lf_markings && { icon: Sparkles, label: t("lfd_markings"), value: listing.lf_markings, testid: "lf-fact-markings", wide: true },
  ].filter(Boolean);
  if (!facts.length) return null;
  return (
    <div className="mt-5 grid sm:grid-cols-2 gap-3" data-testid="lf-facts">
      {facts.map((f) => <Fact key={f.testid} {...f} />)}
    </div>
  );
};
